// The budgets list's "Forecast / month" column.
//
// It sits beside Amount, which shows the budget normalised to a monthly figure
// (formatAmountPrimary → normalizeToMonthly). The forecast has to be normalised the SAME
// way or the two numbers in adjacent columns are not comparable, which is the only thing
// the column is for: is this budget tracking above or below plan?
//
// The source is administration.budget_eval_daily.forecast_total — the projected total of
// the budget's CURRENT period (actuals to date plus the forecast for its remaining days),
// the same basis the detail page's per-period column shows. So a quarterly budget's
// forecast_total covers a quarter and is divided by three, exactly as its amount is.
// formatters.formatMoney, NOT allocationUtils.formatMoney. Two functions share that name
// and differ in precision: this one renders whole currency units, the other two decimals.
// The Amount column beside this one formats through formatters (via formatAmountPrimary),
// so using the same function is what keeps "€ 147 / month" next to "€ 167 / month" instead
// of pairing a rounded amount with a two-decimal forecast.
import { formatMoney } from "../formatters";

// MONTHS_PER_PERIOD converts ONE period's total to a monthly figure.
//
// Deliberately NOT formatters.normalizeToMonthly, even though the Amount column beside
// this one uses it and both end up "per month". That function takes a budget's
// total_amount_cents — the total across EVERY period — so for a monthly budget with no
// period count it divides by 12 on the assumption the budget spans a year. Feeding it one
// period's total understates by 12x (a €147.27 month rendered as €12.27). Same
// destination, different starting point.
const MONTHS_PER_PERIOD = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
  rolling_90d: 3,
};

// monthsPerPeriod defaults to 1: an unrecognised period type is more likely a new
// short-period type than a yearly one, and treating a period as a month leaves the number
// honest at its own granularity rather than dividing it by a guess.
export function monthsPerPeriod(periodType) {
  if (!periodType) return 1;
  return MONTHS_PER_PERIOD[String(periodType).toLowerCase()] || 1;
}

// forecastsByBudgetId indexes the endpoint's rows by budget id.
//
// Tolerates every missing level: the forecasts load independently of the budgets, so they
// are simply absent on first paint, and the table must render without them.
export function forecastsByBudgetId(payload) {
  const rows = payload?.budgetForecasts?.forecasts;
  if (!Array.isArray(rows)) return {};
  const out = {};
  for (const row of rows) {
    if (row && row.budget_id != null) out[String(row.budget_id)] = row;
  }
  return out;
}

// monthlyForecastCents converts one evaluation row into the monthly-equivalent cents the
// column renders. Returns null when there is nothing to show, which the column renders
// as "-" rather than as a confident zero.
//
// forecast_total arrives in plain currency units (147.27) while formatMoney works in
// cents, hence the ×100. A genuine 0 stays 0 — a budget whose scope matches no cost is a
// real zero, not an unknown.
export function monthlyForecastCents(row, periodType) {
  if (!row || typeof row.forecast_total !== "number") return null;
  const cents = Math.round(row.forecast_total * 100);
  return Math.round(cents / monthsPerPeriod(periodType));
}

// formatMonthlyForecast renders the cell. "/ month" matches the Amount column's wording
// exactly, so the pair reads as one comparison rather than two unrelated figures.
export function formatMonthlyForecast(row, periodType, currency) {
  const cents = monthlyForecastCents(row, periodType);
  if (cents == null) return null;
  return `${formatMoney(cents, currency)} / month`;
}

// forecastAsOfTitle qualifies the number with the evaluation's date. The job refreshes
// through the day, so a stale date means the job stopped — not that the budget stopped
// spending, which is the wrong conclusion to invite.
export function forecastAsOfTitle(row) {
  if (!row?.as_of) return undefined;
  const day = String(row.as_of).slice(0, 10);
  return `Projected from the current period's actuals plus its remaining forecast, evaluated ${day}`;
}
